import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import { Loader2 } from 'lucide-react';

const statusBadge = (s) => ({ active: 'bg-green-100 text-green-800', frozen: 'bg-blue-100 text-blue-800', pending: 'bg-amber-100 text-amber-800', expired: 'bg-gray-100 text-gray-700', cancelled: 'bg-red-100 text-red-800' }[s] || 'bg-gray-100 text-gray-700');

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'expiring', label: 'Expiring Soon' },
];

const MembershipsPage = () => {
  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (tab === 'expiring') res = await api.get('/memberships/expiring-soon', { params: { days: 14 } });
      else res = await api.get('/memberships', { params: { status: tab === 'active' ? 'active' : undefined, limit: 100 } });
      setRows(res.data);
    } catch (err) {
      toast({ title: 'Failed to load memberships', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [tab, toast]);

  useEffect(() => { load(); }, [load]);

  const action = async (id, act) => {
    try {
      await api.post(`/memberships/${id}/${act}`, act === 'freeze' ? { freeze_days: 7, reason: 'Admin freeze' } : {});
      const pastTense = { freeze: 'frozen', unfreeze: 'unfrozen', renew: 'renewed', cancel: 'cancelled' }[act] || `${act}d`;
      toast({ title: `Membership ${pastTense}` });
      load();
    } catch (err) { toast({ title: `${act} failed`, description: formatApiError(err), variant: 'destructive' }); }
  };

  return (
    <div data-testid="admin-memberships">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Memberships</h1>
        <p className="text-gray-500 text-sm mt-1">Manage enrollments, freezes, renewals</p>
      </div>

      <div className="inline-flex bg-white rounded-xl p-1 shadow-sm border border-gray-100 mb-4">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid={`membership-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Card className="shadow-md border-0 bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="memberships-empty">No memberships found.</div>
          ) : (
            <Table data-testid="memberships-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ends</TableHead>
                  <TableHead>Remaining</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium cursor-pointer hover:text-blue-600" onClick={() => navigate(`/admin-dashboard/members/${m.swimmer_id}`)}>{m.swimmer_name || '—'}</TableCell>
                    <TableCell>{m.plan_name}</TableCell>
                    <TableCell><Badge className={statusBadge(m.status)}>{m.status}</Badge></TableCell>
                    <TableCell>{m.end_date?.slice(0, 10)}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {m.sessions_remaining != null ? `${m.sessions_remaining} sessions` : m.hours_remaining != null ? `${m.hours_remaining} hrs` : '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {m.status === 'active' && <Button size="sm" variant="outline" onClick={() => action(m.id, 'freeze')} data-testid={`freeze-membership-${m.id}`}>Freeze</Button>}
                      {m.status === 'frozen' && <Button size="sm" variant="outline" onClick={() => action(m.id, 'unfreeze')} data-testid={`unfreeze-membership-${m.id}`}>Unfreeze</Button>}
                      {['active', 'frozen', 'expired'].includes(m.status) && <Button size="sm" variant="outline" onClick={() => action(m.id, 'renew')} data-testid={`renew-membership-${m.id}`}>Renew</Button>}
                      {['active', 'frozen'].includes(m.status) && <Button size="sm" variant="outline" className="text-red-600" onClick={() => action(m.id, 'cancel')} data-testid={`cancel-membership-${m.id}`}>Cancel</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipsPage;
